import React, { useEffect, useState } from "react";
import { functions } from "../../firebase";
import { 
  Paper, 
  makeStyles, 
  Box, 
  Typography, 
  Divider, 
  Button  // Make sure Button is included here
} from "@material-ui/core";
import FacebookIcon from "@material-ui/icons/Facebook";
import InstagramIcon from "@material-ui/icons/Instagram";
import TwitterIcon from "@material-ui/icons/Twitter";
import YouTubeIcon from "@material-ui/icons/YouTube";
import SoundcloudIcon from "@material-ui/icons/CloudQueue";
import FlagIcon from "@material-ui/icons/Flag";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { Link, useLocation } from "react-router-dom";
import sortBy from "lodash/sortBy";
import getFlagEmoji from "../../utils/getFlagEmoji";

const fetchAccountInfo = functions.httpsCallable("getAccountGroups");

const useStyles = makeStyles((theme) => ({
  root: {
    overflow: 'hidden',
    width: '100%',
  },
  detail: {
    flexDirection: "column",
    display: "flex",
    padding: theme.spacing(1.5, 2),
  },
  emailText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    display: 'block',
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: theme.spacing(0.5),
    width: '100%',
  },
  pais: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flexGrow: 1,
  },
  flag: {
    display: "flex",
    alignItems: "center",
    fontSize: "1.1rem",
    minWidth: 24,
  },
  placeholderFlag: {
    color: theme.palette.grey[400],
    fontSize: '1rem',
  },
  redes: {
    display: "flex",
    marginLeft: theme.spacing(1),
    flexShrink: 0,
  },
  sn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.8em",
    color: theme.palette.grey[400],
    marginLeft: theme.spacing(0.5),
  },
  selected: {
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.action.selected,
  },
  accountLink: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  sectionTitle: {
    fontWeight: 500,
    padding: theme.spacing(2, 2, 1),
    marginBottom: theme.spacing(2),
  },
  
  accountsSection: {
    padding: 0,
  },
  placeholder: {
    padding: theme.spacing(1.5, 2),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  countryName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  showMoreButton: {
    padding: theme.spacing(1, 0), // Reduce left padding to 0
    textTransform: 'none',
    color: theme.palette.primary.main,
    justifyContent: 'flex-end',
    fontSize: '0.875rem',
    width: '100%',
    borderRadius: 0,
    marginBottom: theme.spacing(0), // Add bottom margin for spacing
  },
  showMoreIcon: {
    marginRight: theme.spacing(1),
    fontSize: '1.2rem',
  },
  showMoreText: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1), // Add right margin to the text container
  },
  infoContent: {
    padding: theme.spacing(0, 2, 2),
    wordBreak: 'break-word',
    position: 'relative',
  },
  list: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  listItem: {
    marginBottom: theme.spacing(0.5),
  }
}));

const INITIAL_ACCOUNTS_LIMIT = 5;
const INITIAL_ARTISTS_LIMIT = 10;
const INITIAL_LABELS_LIMIT = 10;

const UploaderInfo = ({ email }) => {
  const [info, setInfo] = useState(null);
  const [activeEmail, setActiveEmail] = useState("");
  const [showAllAccounts, setShowAllAccounts] = useState(false);
  const [showAllArtists, setShowAllArtists] = useState(false);
  const [showAllLabels, setShowAllLabels] = useState(false);
  const location = useLocation();
  const classes = useStyles();

  // Get the current active email from URL query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryEmail = searchParams.get("q");
    if (queryEmail && queryEmail.includes("@")) {
      setActiveEmail(queryEmail);
    } else {
      setActiveEmail(email);
    }
  }, [location.search, email]);

  // Fetch account info only when the main email changes
  useEffect(() => {
    const getInfo = async () => {
      if (!email) return;
      
      try {
        const { data } = await fetchAccountInfo([email]);

        if (!data) return;

        const INITIAL = {
          accounts: [],
          artists: [],
          labels: [],
        };

        const accountData = data[0].reduce((acc, curr) => {
          const {
            email,
            country,
            countryISOCode,
            youtube,
            facebook,
            instagram,
            twitter,
            soundcloud,
          } = curr;

          acc.accounts.push({
            email,
            country: countryISOCode,
            countryName: country,
            redes: [youtube, facebook, instagram, twitter, soundcloud].filter(Boolean),
          });

          acc.artists.push(...curr.catalog.artists);
          acc.labels.push(...curr.catalog.labels);

          return acc;
        }, INITIAL);

        setInfo(accountData);
      } catch (error) {
        console.error("Error fetching account info:", error);
      }
    };

    // Initialize with at least the current email
    if (!info && email) {
      setInfo({
        accounts: [{
          email,
          country: "",
          countryName: "",
          redes: []
        }],
        artists: [],
        labels: []
      });
    }

    getInfo();
  }, [email]);

  // If we don't have any info yet, but we have an email, show just that email
  if (!info && email) {
    return (
      <Paper className={classes.root}>
        
        <Box className={classes.accountsSection}>
          <Box className={classes.selected}>
            <Box className={classes.detail}>
              <Typography className={`${classes.accountLink} ${classes.emailText}`}>
                {email}
              </Typography>
              <div className={classes.infoRow}>
                <div className={classes.pais}>
                  <span className={classes.flag}>
                    <FlagIcon className={classes.placeholderFlag} fontSize="small" />
                  </span>
                </div>
              </div>
            </Box>
          </Box>
        </Box>
        
        <Divider />
        
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          Artistas
        </Typography>
        
        <Box className={classes.infoContent}>
          <Typography variant="body2" className={classes.placeholder}>
            ... 
          </Typography>
        </Box>
        
        <Divider />
        
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          Labels
        </Typography>
        
        <Box className={classes.infoContent}>
          <Typography variant="body2" className={classes.placeholder}>
            ...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!info || !email) return null;

  const linkIt = (url) => {
    if (!url) return null;

    let fullUrl = url;

    // add https if missing
    if (!url.includes("http")) fullUrl = `https://${url}`;

    // icon
    let icon;
    if (url.includes("facebook")) icon = <FacebookIcon fontSize="small" />;
    if (url.includes("instagram")) icon = <InstagramIcon fontSize="small" />;
    if (url.includes("twitter")) icon = <TwitterIcon fontSize="small" />;
    if (url.includes("youtube")) icon = <YouTubeIcon fontSize="small" />;
    if (url.includes("soundcloud")) icon = <SoundcloudIcon fontSize="small" />;

    return (
      <a className={classes.sn} key={url} target="_blank" href={fullUrl} rel="noopener noreferrer">
        {icon}
      </a>
    );
  };

  const detailIt = (user) => {
    const { country, redes, countryName } = user;
    const userEmail = user.email;
    const isActive = userEmail === activeEmail;

    return (
      <Box className={isActive ? classes.selected : ""} key={userEmail}>
        <Box className={classes.detail}>
          <Link 
            className={`${classes.accountLink} ${classes.emailText}`}
            to={`/albums?q=${userEmail}`}
            onClick={() => setActiveEmail(userEmail)}
            title={userEmail} // Add title for hover tooltip on truncated emails
          >
            {userEmail}
          </Link>
          <div className={classes.infoRow}>
            <div className={classes.pais}>
              <span className={classes.flag}>
                {country ? 
                  getFlagEmoji(country) : 
                  <FlagIcon className={classes.placeholderFlag} fontSize="small" />
                }
              </span>
              {countryName && (
                <span className={classes.countryName}>{countryName}</span>
              )}
            </div>
            {redes && redes.length > 0 && (
              <div className={classes.redes}>{redes.map(linkIt)}</div>
            )}
          </div>
        </Box>
      </Box>
    );
  };

  const normalizeString = (str) => {
  if (!str) return '';
  return str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
};

// Get unique artists
const getArtistsUnique = (artists) => {
  if (!artists || !artists.length) return [];
  
  const unique = [...new Set(artists.map(a => a.name))];
  return sortBy(unique)
    .map(name => name.trim())
    .filter(name => name);
};

// Get unique labels
const getLabelsUnique = (labels) => {
  if (!labels || !labels.length) return [];
  
  const unique = [...new Set(labels)];
  return sortBy(unique)
    .map(label => label.trim())
    .filter(label => label);
};

// Calculate the unique lists
const uniqueArtists = getArtistsUnique(info.artists);
const uniqueLabels = getLabelsUnique(info.labels);
  return (
    <Paper className={classes.root}>
      <Box className={classes.accountsSection}>
        {/* Calculate visible accounts */}
        {(showAllAccounts ? info.accounts : info.accounts.slice(0, INITIAL_ACCOUNTS_LIMIT)).map(detailIt)}
        
        {/* Show More button for accounts */}
        {info.accounts.length > INITIAL_ACCOUNTS_LIMIT && (
          <Button 
            className={classes.showMoreButton}
            style={{paddingRight: "1rem"}}
            onClick={() => setShowAllAccounts(!showAllAccounts)}
          >
            <span className={classes.showMoreText}>
              {showAllAccounts ? (
                <>
                  <ExpandLessIcon className={classes.showMoreIcon} />
                  Mostrar menos
                </>
              ) : (
                <>
                  <ExpandMoreIcon className={classes.showMoreIcon} />
                  Mostrar {info.accounts.length - INITIAL_ACCOUNTS_LIMIT} más
                </>
              )}
            </span>
          </Button>
        )}
      </Box>

      <Divider />

      <Typography variant="subtitle2" className={classes.sectionTitle}>
        Artistas
      </Typography>
      
      <Box className={classes.infoContent}>
        {uniqueArtists.length > 0 ? (
          <>
            <ul className={classes.list}>
              {/* Calculate visible artists */}
              {(showAllArtists ? uniqueArtists : uniqueArtists.slice(0, INITIAL_ARTISTS_LIMIT))
                .map((artist, index) => (
                  <li key={index} className={classes.listItem}>{artist}</li>
                ))}
            </ul>
            
            {/* Show More button for artists */}
            {uniqueArtists.length > INITIAL_ARTISTS_LIMIT && (
              <Button 
                className={classes.showMoreButton}
                onClick={() => setShowAllArtists(!showAllArtists)}
              >
                <span className={classes.showMoreText}>
                  {showAllArtists ? (
                    <>
                      <ExpandLessIcon className={classes.showMoreIcon} />
                      Mostrar menos
                    </>
                  ) : (
                    <>
                      <ExpandMoreIcon className={classes.showMoreIcon} />
                      Mostrar {uniqueArtists.length - INITIAL_ARTISTS_LIMIT} más
                    </>
                  )}
                </span>
              </Button>
            )}
          </>
        ) : (
          <Typography variant="body2">No hay artistas</Typography>
        )}
      </Box>

      <Divider />
      <Typography variant="subtitle2" className={classes.sectionTitle}>
        Labels
      </Typography>
      
      <Box className={classes.infoContent}>
        {uniqueLabels.length > 0 ? (
          <>
            <ul className={classes.list}>
              {/* Calculate visible labels */}
              {(showAllLabels ? uniqueLabels : uniqueLabels.slice(0, INITIAL_LABELS_LIMIT))
                .map((label, index) => (
                  <li key={index} className={classes.listItem}>{label}</li>
                ))}
            </ul>
            
            {/* Show More button for labels */}
            {uniqueLabels.length > INITIAL_LABELS_LIMIT && (
              <Button 
                className={classes.showMoreButton}
                onClick={() => setShowAllLabels(!showAllLabels)}
              >
                <span className={classes.showMoreText}>
                  {showAllLabels ? (
                    <>
                      <ExpandLessIcon className={classes.showMoreIcon} />
                      Mostrar menos
                    </>
                  ) : (
                    <>
                      <ExpandMoreIcon className={classes.showMoreIcon} />
                      Mostrar {uniqueLabels.length - INITIAL_LABELS_LIMIT} más
                    </>
                  )}
                </span>
              </Button>
            )}
          </>
        ) : (
          <Typography variant="body2">No hay labels</Typography>
        )}
      </Box>
    </Paper>
  );
};

export default UploaderInfo;